import { Backdrop, Button, Checkbox, CircularProgress, FormControl, FormHelperText, Grid, IconButton, InputAdornment, InputLabel, ListItemText, MenuItem, OutlinedInput, Select, TextField, Typography } from "@mui/material"
import { ProtectedRoutes } from "../../common/ProtectedRoute";
import MenuDrawer from "../Dashboard/components/MenuDrawer";
import React, { useEffect, useState } from "react";
import { defaultTheme } from "../../styles/Theme";
import { Box } from "@mui/system";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { createUser, getUserByEmail, getUserById, getUserByUsername, updateUser } from "../../services/AuthService";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { STATUS_CODE_SUCCESS } from "../../utils/Constants";
import { enqueueSnackbar } from "notistack";
import { getUserRoles } from "../../store/reducers/role"
import { useSelector } from "react-redux";
import { useDbData } from "../Dashboard/hooks/useDbData";
import { getCountries, getRegionCountries } from "../../utils/CountryUtils";

const CreateUser = ({allowedRoles}) => {

  const { id } = useParams();

  const userRoles = useSelector(getUserRoles);
  const loggedInUser = useSelector((state) => state.user);

  const styles = {
    headerStyle: {
      fontWeight: 'bold',
      marginTop: '2%',
      marginBottom: '2%',
      color: defaultTheme.palette.primary.main
    },
    dashboardStyle: {
      backgroundColor: defaultTheme.palette.primary.contrastText,
      marginBottom: '2%'
    },
    containerStyle: {
      height: '80vh'
    }
  }

  const ITEM_HEIGHT = 48;
  const ITEM_PADDING_TOP = 8;
  const MenuProps = {
    PaperProps: {
      style: {
        maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
        width: 250,
      },
    },
  };



  const navigate = useNavigate();
  const { distinctSearchFilters } = useDbData();
  const regionCountries = getRegionCountries(userRoles);

  const ROLE_OPTIONS = {
    admin: 'Admin',
    logistics_manager: 'Logistics Manager',
    logistics_agent: 'Logistics Agent',
  };

  const [view, setView] = React.useState('Dashboard');
  const [drawerState, setDrawerState] = React.useState('closed');
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    email: "",
    username: "",
    password: "",
    selectedCountries: [],
    roleName: ""
  });
  const [isCountriesDisabled, setIsCountriesDisabled] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({
    email: false,
    username: false,
    password: false,
    selectedCountries: false,
    roleName: false
  });
  const [errorMessages, setErrorMessages] = useState({
    email: "",
    username: "",
    password: ""
  });

  const generatePassword = (length = 8) => {
    const lowerCaseChars = 'abcdefghijklmnopqrstuvwxyz';
    const upperCaseChars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const specialChars = '!@#$%&*_.?';
    const numbers = '0123456789';

    // Ensure at least one character from each category
    const getRandomChar = (chars) => chars[Math.floor(Math.random() * chars.length)];

    const mandatoryChars = [
      getRandomChar(lowerCaseChars),
      getRandomChar(upperCaseChars),
      getRandomChar(specialChars),
      getRandomChar(numbers),
    ];

    // Fill the rest of the password with random characters from all categories
    const allChars = lowerCaseChars + upperCaseChars + specialChars + numbers;
    const remainingLength = length - mandatoryChars.length;

    let password = mandatoryChars;

    for (let i = 0; i < remainingLength; i++) {
      password.push(getRandomChar(allChars));
    }

    // Shuffle the password to avoid predictable patterns
    password = password.sort(() => Math.random() - 0.5).join('');

    return password;
  };

  const getAvailableRoles = () => {
    if (userRoles.includes('admin')) {
      return Object.entries(ROLE_OPTIONS); // Show all roles
    } else if (userRoles.includes('logistics_manager')) {
      return Object.entries(ROLE_OPTIONS).filter(([key]) =>
        ['logistics_manager', 'logistics_agent'].includes(key)
      );
    }
    return []; // No available roles
  };

  const availableRoles = getAvailableRoles();

  const getRoleName = (roles) => {
    let roleNames = ["admin", "logistics_manager", "logistics_agent"];
    
    let roleExists = [];
    if(roles){
      roles.forEach(role => {
        if(role.name && roleNames.includes(role.name)){
          roleExists.push(role.name);
        }
      });
    }
    if (roleExists.includes("admin")) {
      return "admin";
    } else if (roleExists.includes("logistics_manager")) {
      return "logistics_manager";
    } else if (roleExists.includes("logistics_agent")) {
      return "logistics_agent";
    }

    return "Not Available";
  }

  useEffect(() => {
    setFormData((prev) => ({ ...prev, password: generatePassword() }));
  }, []);

  useEffect(() => {
    if (formData.roleName === 'admin') {
      setFormData((prev) => ({
        ...prev,
        selectedCountries: regionCountries?.map((c) => c.value),
      }));
      setIsCountriesDisabled(true);
    } else {
      setIsCountriesDisabled(false);
    }
  }, [formData.roleName]);

  useEffect(() => {
    if (id) {
      (async () => {
        try {
          setLoading(true);
          const response = await getUserById(id, loggedInUser);
          if (response.statusCode == STATUS_CODE_SUCCESS) {
            const countries = getCountries(response.data.user.roles.map(role => role.name));
            setFormData({
              username: response.data.user.username,
              email: response.data.user.email,
              selectedCountries: countries.includes("admin") ? regionCountries?.map(country => country.value) : countries,
              password: '',
              roleName: getRoleName(response.data.user.roles),
            });
          } else {
            enqueueSnackbar(`${response.message}. Redirecting to Create User`, { autoHideDuration: 2000, variant: 'error' })
            setTimeout(() => {
              navigate('/user/create');
            }, 2000);
          }
        } catch (error) {
          enqueueSnackbar('Error fetching user details. Try again later.', { autoHideDuration: 2000, variant: 'error' })
        } finally {
          setLoading(false);
        }
      })();
    }
  }, [id, distinctSearchFilters.countries]);

  const hasPermission = userRoles.some(role => allowedRoles.includes(role));
  if (!hasPermission) {
    enqueueSnackbar('Access Denied', { autoHideDuration: 2000, variant: 'error' });
    return <Navigate to="/dashboard" replace />;
  }

  const handleCountryChange = (event) => {
    const selectedValues = event.target.value;
    setFormData((prev) => ({ ...prev, selectedCountries: selectedValues }));
    setErrors((prev) => ({ ...prev, selectedCountries: selectedValues.length === 0 }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!id) {
      if (!formData.email) {
        newErrors.email = true;
      } else if (!/^\S+@\S+\.\S+$/.test(formData.email)) {
        newErrors.email = true;
      }
      if (!formData.username) newErrors.username = true;
      if (formData.selectedCountries.length === 0) {
        newErrors.selectedCountries = true;
      }
      if (!formData.roleName) newErrors.roleName = true;
    }

    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{8,}$/;
    if (!formData.password) {
      newErrors.password = true;
    } else if (!passwordRegex.test(formData.password)) {
      setErrorMessages((prev) => ({ ...prev, password: 'Password does not meet the required criteria' }));
      enqueueSnackbar(
        'Password must be at least 8 characters and include uppercase, lowercase, number, and special character',
        { autoHideDuration: 2000, variant: 'error' }
      );
      newErrors.password = true;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0; // Returns true if no errors
  };

  const handleTextFieldChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: value.trim() === "" }));
    setErrorMessages((prev) => ({ ...prev, [name]: "" }));
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setErrors({});
      setErrorMessages({});

      if (!validateForm()) {
        return;
      }

      let userResponse;

      if (id) {
        const updateUserRequest = {
          "password": formData.password,
          "roleName": formData.roleName,
          "selectedCountries": formData.selectedCountries
        }
        userResponse = await updateUser(id, updateUserRequest, loggedInUser);
      } else {
        const emailResponse = await getUserByEmail(formData.email, loggedInUser);
        if (emailResponse.message === "success") {
          setErrorMessages((prev) => ({ ...prev, email: 'Email is already registered' }));
          setErrors((prev) => ({ ...prev, email: true }));
          return;
        }

        const usernameResponse = await getUserByUsername(formData.username, loggedInUser);
        if (usernameResponse.message === "success") {
          setErrors((prev) => ({ ...prev, username: true }));
          setErrorMessages((prev) => ({ ...prev, username: 'Username is already taken' }));
          return;
        }

        userResponse = await createUser(formData, loggedInUser);
      }
      if (userResponse.data.message === "success") {
        enqueueSnackbar(`User ${id ? 'updated' : 'created'} successfully`, { autoHideDuration: 2000, variant: 'success' });
        setTimeout(() => {
          navigate('/user-management');
        }, 2000);
        setFormData({
          email: "",
          username: "",
          password: generatePassword(),
          selectedCountries: []
        });
        setShowPassword(false);
      } else {
        enqueueSnackbar(`Error while ${id ? 'updating' : 'creating'} a user`, { autoHideDuration: 2000, variant: 'error' })
      }
    } catch (error) {
      console.log(error);
      enqueueSnackbar(`Error while ${id ? 'updating' : 'creating'} a user`, { autoHideDuration: 2000, variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async () => {
    navigate('/user-management');
  }

  const onViewSelected = (view) => {
    setView(view)
  }

  const onDrawerOpen = () => {
    setDrawerState('open')
  }

  const onDrawerClose = () => {
    setDrawerState('closed')
  }

  return (
    <>
      <ProtectedRoutes>
        <MenuDrawer onDrawerOpen={onDrawerOpen} onDrawerClose={onDrawerClose} onViewSelected={onViewSelected} >
          <Typography variant="h5" style={styles.headerStyle}>{id ? 'Edit User' : 'Create User'}</Typography>
          <div style={styles.dashboardStyle}>
            <Box
              component="form"
              sx={{ '& .MuiTextField-root': { m: 1 } }}
              noValidate
              autoComplete="off"
            >
              <div style={{ padding: 10 }}>
                <FormControl sx={{ m: 1, width: 300 }} error={errors.selectedCountries}>
                  <InputLabel>Country</InputLabel>
                  <Select
                    multiple
                    name="selectedCountries"
                    value={formData.selectedCountries ? formData.selectedCountries : []}
                    onChange={handleCountryChange}
                    error={errors.selectedCountries}
                    input={<OutlinedInput label="Country" />}
                    renderValue={(selected) => selected.join(', ')}
                    MenuProps={MenuProps}
                    disabled={isCountriesDisabled}
                  >
                    {regionCountries?.map((country) => (
                      <MenuItem key={country.value} value={country.value}>
                        <Checkbox checked={formData.selectedCountries?.includes(country.value)} />
                        <ListItemText primary={country.name} />
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.selectedCountries && <FormHelperText>At least one country is required</FormHelperText>}
                </FormControl>
                <TextField
                  name="email"
                  value={formData.email}
                  onChange={handleTextFieldChange}
                  error={errors.email}
                  InputProps={{ disabled: !!id }}
                  helperText={errorMessages.email || (errors.email ? "Valid email is required" : "")}
                  label="Email ID"
                />
                <TextField
                  label="User Name"
                  name="username"
                  value={formData.username}
                  onChange={handleTextFieldChange}
                  error={errors.username}
                  InputProps={{ disabled: !!id }}
                  helperText={errorMessages.username || (errors.username ? "Username is required" : "")}
                />
                <TextField
                  label="Password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={handleTextFieldChange}
                  error={errors.password}
                  helperText={errorMessages.password || (errors.password ? "Password is required" : "")}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={togglePasswordVisibility} edge="end">
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />
                <Grid container>
                  <FormControl sx={{ m: 1, width: 300 }} error={errors.roleName}>
                    <InputLabel id="role-label">Role</InputLabel>
                    <Select
                      labelId="role-label"
                      label="Role"
                      name="roleName"
                      id="roleName"
                      value={formData.roleName}
                      onChange={handleChange}
                    >
                      {availableRoles.map(([key, value]) => (
                        <MenuItem key={key} value={key}>
                          {value}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.roleName && <FormHelperText>Role is required</FormHelperText>}
                  </FormControl>
                </Grid>
                <Grid container marginLeft={1}>
                  <Button
                    variant="contained"
                    onClick={handleSubmit}
                  >
                    {id ? 'Update' : 'Save'}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={handleCancel}
                    style={{ marginLeft: 4 }}
                  >
                    Cancel
                  </Button>
                </Grid>
              </div>
            </Box>
            <Backdrop open={loading} sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}>
              <CircularProgress color="inherit" />
            </Backdrop>
          </div>
        </MenuDrawer>
      </ProtectedRoutes>
    </>
  );
}

export default CreateUser